import React, { useState, useEffect } from "react";
import { useRecoilValue } from "recoil";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography } from "@mui/material";
import { ortexColorState, textColorState, userState } from "@/lib/store";
import { sendFeedback } from "@/controllers/feedback-form";
import { useIdle, useNetworkState, useOrientation, useWindowScroll, useWindowSize } from "react-use";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useRouter } from "next/router";

type FeedbackFormProps = {
    isOpen: boolean;
    onClose: (isOpen: boolean) => void;
    moduleId: string;
};

const FeedbackForm = ({ isOpen, onClose, moduleId }: FeedbackFormProps) => {
    const user = useRecoilValue(userState);
    const textColor = useRecoilValue(textColorState);
    const ortexColor = useRecoilValue(ortexColorState);

    const [subject, setSubject] = useState("");
    const [comment, setComment] = useState("");

    const [step, setStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    const router = useRouter();
    const pageId = router.asPath;

    const meta = {
        theme: user?.settings.theme,
        email: user?.email,
        idle: useIdle(),
        network_state: useNetworkState(),
        orientation: useOrientation(),
        scrolling: null,
        window_scroll: useWindowScroll(),
        window_size: useWindowSize(),
        browser: typeof window !== "undefined" ? navigator?.userAgent : "",
        timestamp: new Date().toISOString(),
        referrer: typeof document !== "undefined" ? document.referrer : "",
        viewport: {
            width: typeof window !== "undefined" ? window.innerWidth : 0,
            height: typeof window !== "undefined" ? window.innerHeight : 0,
        },
        language: typeof navigator !== "undefined" ? navigator.language : "",
    };

    useEffect(() => {
        if (step === 1) {
            const timer = setTimeout(() => {
                onClose(false);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [step, onClose]);

    useEffect(() => {
        if (isOpen) {
            setStep(0);
            setSubject("");
            setComment("");
            setIsLoading(false);
            setIsError(false);
        }
    }, [isOpen]);

    const handleSubmit = (event) => {
        event.preventDefault();
        setIsLoading(true);

        sendFeedback(moduleId, pageId, meta, { subject, comment })
            .then(() => {
                setStep(1);
            })
            .catch(() => {
                setIsError(true);
                setStep(1);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <div>
            <Dialog open={isOpen} onClose={() => onClose(false)} maxWidth="xs" fullWidth>
                <DialogTitle id="alert-dialog-title">{"Send Feedback"}</DialogTitle>
                {step === 0 ? (
                    <DialogContent sx={{ minHeight: "325px" }}>
                        <DialogContentText id="alert-dialog-description">Share your thoughts to help us improve.</DialogContentText>

                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ fontWeight: "bold" }}>Subject</Typography>
                            <input
                                style={{
                                    width: "100%",
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    backgroundColor: "transparent",
                                    color: textColor,
                                    marginTop: "5px",
                                }}
                                placeholder="What is your feedback about?"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                            />
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ fontWeight: "bold" }}>Comment</Typography>
                            <textarea
                                rows={5}
                                style={{
                                    width: "100%",
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                    padding: "8px",
                                    backgroundColor: "transparent",
                                    color: textColor,
                                    marginTop: "5px",
                                }}
                                placeholder="Tell us what you think..."
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            />
                            <Typography variant="body3" sx={{ color: textColor, mt: 1, opacity: 0.8 }}>
                                Noticed something wrong? Tell us why you believe the data may be incorrect, and be as specific as possible.
                            </Typography>
                        </Box>
                    </DialogContent>
                ) : (
                    <DialogContent sx={{ display: "flex", alignItems: "center", minHeight: "325px" }}>
                        <DialogContentText id="alert-dialog-description">
                            {!isError ? <CheckCircleIcon sx={{ color: ortexColor, fontSize: "80px", width: "100%" }} /> : <ErrorOutlineIcon sx={{ color: "red", fontSize: "80px", width: "100%" }} />}
                            <Typography sx={{ textAlign: "center" }}>{!isError ? "Thank you for your feedback! This helps us improve." : "Something went wrong while sending your feedback. Please try again."}</Typography>
                        </DialogContentText>
                    </DialogContent>
                )}
                <DialogActions sx={{ m: 2 }}>
                    {step === 0 && (
                        <>
                            <Button onClick={() => onClose(false)}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={isLoading || !subject.length || !comment.length}
                                sx={{
                                    "&.Mui-disabled": {
                                        color: "grey",
                                    },
                                }}
                            >
                                {isLoading ? "Sending feedback..." : "Submit feedback"}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default FeedbackForm;
